import {
  Avatar,
  Box,
  Paper,
  Theme,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PauseCircleFilledIcon from "@mui/icons-material/PauseCircleFilled";
import ChangeCircleIcon from "@mui/icons-material/ChangeCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { WarningAmber } from "@mui/icons-material";
import LinearProgress from "@mui/material/LinearProgress";
import { ProjectTask } from "../canonical";
import { FilterSortGroup } from "../projectTypes";
import { formatDateOnly, dateToMillis } from "../utils/ProjectDetailsUtilities";

interface TaskPresentation {
  icon: React.ReactElement<any>;
  textDecoration?: string;
  progressColor?: string;
  avatarColor?: string;
  isCompleted: boolean;
}

export function getTaskPresentation(
  theme: Theme,
  status?: number,
): TaskPresentation {
  switch (status) {
    case 1: // New
      return {
        icon: (
          <RadioButtonUncheckedIcon sx={{ color: theme.palette.grey.A700 }} />
        ),
        textDecoration: "none",
        progressColor: theme.palette.info.light,
        avatarColor: theme.palette.grey[400],
        isCompleted: false,
      };
    case 8: // In Progress
      return {
        icon: <ChangeCircleIcon sx={{ color: theme.palette.info.main }} />,
        textDecoration: "none",
        progressColor: theme.palette.info.main,
        avatarColor: theme.palette.info.light,
        isCompleted: false,
      };
    case 5: // Complete
      return {
        icon: <CheckCircleIcon sx={{ color: theme.palette.success.light }} />,
        textDecoration: "line-through",
        progressColor: theme.palette.success.main,
        avatarColor: theme.palette.success.light,
        isCompleted: true,
      };
    case 17: // On Hold
      return {
        icon: (
          <PauseCircleFilledIcon sx={{ color: theme.palette.warning.main }} />
        ),
        textDecoration: "none",
        progressColor: theme.palette.warning.main,
        avatarColor: theme.palette.warning.light,
        isCompleted: false,
      };
    default:
      return {
        icon: <RadioButtonUncheckedIcon color="disabled" />,
        textDecoration: "none",
        progressColor: theme.palette.text.disabled,
        avatarColor: theme.palette.grey[400],
        isCompleted: false,
      };
  }
}

interface ProjectDetailsTaskCardProps {
  task: ProjectTask;
  filterSortGroup: FilterSortGroup;
}

const ProjectDetailsTaskCard = ({
  task,
  filterSortGroup,
}: ProjectDetailsTaskCardProps) => {
  const theme = useTheme();
  const presentation = getTaskPresentation(theme, task.status);
  const assigneeInitials = task.assignedResourceName
    ?.split(" ")
    .map((n) => n[0])
    .join("");

  const endDateMs = dateToMillis(task.endDateTime);
  const isOverdue =
    endDateMs && !presentation.isCompleted && endDateMs < Date.now();

  return (
    <Paper
      key={task.id}
      elevation={0}
      sx={{
        p: 1,
        display: "flex",
        gap: 2,
      }}
    >
      {presentation.icon}
      <Box
        flex={1}
        display="flex"
        flexDirection="column"
        gap={1.25}
        pb={1}
        pr={1}
      >
        <Typography
          sx={{
            textDecoration: presentation.textDecoration,
          }}
        >
          {task.title}
        </Typography>
        {filterSortGroup.grouping?.id === "status" && (
          <Typography variant="caption" color="text.secondary">
            Phase: {task.phase}
          </Typography>
        )}
        {(task.assignedResourceID ?? 0) > 0 && (
          <Box display="flex" flexDirection="row" gap={1}>
            <Avatar
              sx={{
                width: 24,
                height: 24,
                fontSize: 12,
                bgcolor: "action.selected",
              }}
            >
              {assigneeInitials ?? "U"}
            </Avatar>
            <Typography variant="caption" color="text.secondary">
              {task.assignedResourceName || "Unassigned"}
            </Typography>
          </Box>
        )}

        <Tooltip
          title={
            isOverdue
              ? `Overdue since ${formatDateOnly(task.endDateTime)}`
              : ""
          }
          arrow
          disableHoverListener={!isOverdue}
          slotProps={{
            popper: {
              modifiers: [
                {
                  name: "offset",
                  options: {
                    offset: [0, -12],
                  },
                },
              ],
            },
          }}
        >
          <Box>
            <Box display="flex" justifyContent="space-between">
              <Typography variant="caption" color="text.secondary">
                {formatDateOnly(task.startDateTime)}
                {" - "}
                {task.endDateTime ? (
                  <Box
                    component="span"
                    style={{
                      color: isOverdue ? theme.palette.error.main : undefined,
                    }}
                  >
                    {isOverdue && <WarningAmber sx={{ fontSize: 18 }} />}{" "}
                    {formatDateOnly(task.endDateTime)}
                  </Box>
                ) : (
                  ""
                )}
              </Typography>
              {task.completedDateTime && (
                <Typography variant="caption" color="text.secondary">
                  Completed: {formatDateOnly(task.completedDateTime)}
                </Typography>
              )}
            </Box>

            <LinearProgress
              variant="determinate"
              value={task.progress}
              sx={{
                marginTop: 1,
                height: 4,
                borderRadius: 2,
                backgroundColor: theme.palette.action.hover,
                "& .MuiLinearProgress-bar": {
                  borderRadius: 2,
                  backgroundColor: isOverdue
                    ? theme.palette.error.main
                    : undefined,
                },
              }}
            />
          </Box>
        </Tooltip>
      </Box>
    </Paper>
  );
};

export default ProjectDetailsTaskCard;
